import React from "react";
import { skills, techUsage, projects } from "../../data/portfolio";
import { useFilter } from "../../context/FilterContext";
import { scrollToSection } from "../../lib/scrollTo";
import Reveal from "../motion/Reveal";
import AxisReveal from "../motion/AxisReveal";
import Decode from "../motion/Decode";
import Ruler from "../motion/Ruler";

const GROUPS = [
  { key: "languages", label: "Languages" },
  { key: "frameworks", label: "Frameworks" },
  { key: "libraries", label: "Libraries" },
  { key: "tools", label: "Tools" },
];

/**
 * Depth is encoded typographically — size, weight and width scale with
 * proficiency. No bars, no percentages: the field reads as a type specimen.
 *
 * It is also the site's control panel: anything used in shipped work is a
 * button, and selecting it filters the work index in the chapter above.
 */
const scaleFor = (level) => {
  const t = Math.min(Math.max((level - 20) / 72, 0), 1);
  return {
    fontSize: `${(0.95 + t * 1.15).toFixed(3)}rem`,
    fontVariationSettings: `'wdth' ${Math.round(86 + t * 30)}, 'wght' ${Math.round(400 + t * 300)}`,
    lineHeight: 1.12,
  };
};

const tracked = Object.entries(techUsage).sort((a, b) => b[1] - a[1]);
const [mostUsedName, mostUsedCount] = tracked[0] || ["—", 0];
const totalNamed = Object.values(skills).reduce((sum, group) => sum + group.length, 0);

const Skills = () => {
  const { tech, toggle } = useFilter();

  const select = (name) => {
    toggle(name);
    if (tech !== name) scrollToSection("work");
  };

  return (
    <section id="skills" className="chapter chapter-pad relative" aria-labelledby="skills-title">
      <div className="page">
        <div className="flex flex-wrap items-baseline justify-between gap-4 pb-4">
          <h2 id="skills-title" className="t-label">
            <span className="text-flare">04</span> — Field
            <span className="sr-only"> — capabilities</span>
          </h2>
          <Decode as="span" className="t-label" text="Capability field" />
        </div>
        <Ruler ticks={16} />

        <div className="mt-12 grid-12 gap-y-12 md:mt-16">
          <Reveal className="col-span-12 lg:col-span-3">
            <p className="t-body text-dim">
              Scale carries the weight here: the deeper the working knowledge, the larger and wider
              the word is set. Anything that shipped is a control — select it to filter the work.
            </p>
            <div className="hair-t mt-6 flex items-baseline gap-4 pt-4">
              <span className="text-dim" style={scaleFor(20)}>
                learning
              </span>
              <span className="text-fg" style={scaleFor(92)}>
                daily
              </span>
            </div>
          </Reveal>

          <div className="col-span-12 grid gap-x-8 gap-y-12 sm:grid-cols-2 lg:col-span-9 lg:col-start-4 lg:grid-cols-4">
            {GROUPS.map((group) => {
              const items = [...(skills[group.key] || [])].sort((a, b) => b.level - a.level);

              return (
                <Reveal key={group.key} stagger={0.04}>
                  <span className="t-label hair-b block pb-2.5">{group.label}</span>
                  <ul className="mt-4 flex flex-col gap-2.5">
                    {items.map((item) => {
                      const uses = techUsage[item.name] || 0;
                      const selected = tech === item.name;

                      if (!uses) {
                        return (
                          <li key={item.name} data-reveal-item>
                            <span className="block text-dim" style={scaleFor(item.level)}>
                              {item.name}
                            </span>
                          </li>
                        );
                      }

                      return (
                        <li key={item.name} data-reveal-item>
                          <button
                            type="button"
                            onClick={() => select(item.name)}
                            aria-pressed={selected}
                            className="group block w-full text-left"
                            data-cursor-label={selected ? "Clear" : "Filter"}
                          >
                            <span
                              className={`block transition-colors duration-200 ease-out ${
                                selected ? "text-flare" : "text-fg group-hover:text-flare"
                              }`}
                              style={scaleFor(item.level)}
                            >
                              {item.name}
                            </span>
                            <span className="mt-0.5 flex items-center gap-2">
                              <span
                                className="block h-px transition-all duration-300 ease-out"
                                style={{
                                  width: selected ? 34 : 0,
                                  background: "var(--flare)",
                                }}
                              />
                              <span
                                className={`t-label transition-opacity duration-200 ${
                                  selected ? "opacity-100" : "opacity-0 group-hover:opacity-100"
                                }`}
                              >
                                {uses} project{uses > 1 ? "s" : ""}
                              </span>
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </Reveal>
              );
            })}
          </div>
        </div>

        {/* Closing readouts — all three values are computed, not written */}
        <Reveal stagger={0.07} className="hair-t mt-16 grid gap-x-8 gap-y-5 pt-5 sm:grid-cols-3">
          <div data-reveal-item>
            <span className="t-label block">[ Named here ]</span>
            <AxisReveal as="span" className="mt-1 block text-sm text-fg" wdth={100} wght={500}>
              {totalNamed} technologies
            </AxisReveal>
          </div>
          <div data-reveal-item>
            <span className="t-label block">[ In shipped work ]</span>
            <AxisReveal as="span" className="mt-1 block text-sm text-fg" wdth={100} wght={500}>
              {tracked.length} across {projects.length} projects
            </AxisReveal>
          </div>
          <div data-reveal-item>
            <span className="t-label block">[ Most used ]</span>
            <AxisReveal as="span" className="mt-1 block text-sm text-fg" wdth={100} wght={500}>
              {mostUsedName} — {mostUsedCount} projects
            </AxisReveal>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default Skills;
